"use client";

import { motion } from "framer-motion";
import {
  ArrowRight,
  Award,
  Users,
  Globe,
  Shield,
  CheckCircle,
  MapPin,
  Anchor,
  Briefcase,
  AlertTriangle,
} from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";

const Home = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const services = [
    {
      icon: Users,
      title: "Training & Certifications",
      description:
        "Structured learning paths for seafarers and end-to-end support for maritime and offshore certifications.",
      link: "/courses",
      highlights: [
        "STCW Compliant",
        "International Recognition",
        "Career Support",
      ],
    },
    {
      icon: Anchor,
      title: "Ship Chartering",
      description:
        "Technical and commercial management services designed to protect your asset and support long-term performance.",
      link: "/ship-chartering",
      highlights: [
        "Technical Management",
        "HSQE Systems",
        "Transparent Reporting",
      ],
    },
    {
      icon: Briefcase,
      title: "Crew Management",
      description:
        "Recruitment, planning, documentation, payroll and welfare with safety and retention as core focus.",
      link: "/crew-management",
      highlights: ["Verified Sourcing", "Compliance", "Welfare Support"],
    },
    {
      icon: AlertTriangle,
      title: "Technical Services",
      description:
        "Ship preparation, inspections, modifications and ongoing technical support to keep vessels safe and efficient.",
      link: "/technical-services",
      highlights: ["Pre-purchase Assessment", "Dry-dock Management", "Support"],
    },
    {
      icon: Globe,
      title: "Registry & Survey",
      description:
        "Guidance and coordination for flag registration, change of flag and associated surveys.",
      link: "/registry-survey",
      highlights: ["Flag Selection", "Documentation", "Survey Coordination"],
    },
    {
      icon: Shield,
      title: "Ship Insurance",
      description:
        "Assistance in arranging and managing marine insurances with the right level of cover.",
      link: "/ship-insurance",
      highlights: ["Risk Assessment", "Claims Support", "Coverage Guidance"],
    },
  ];

  const stats = [
    { icon: Award, value: "15+", label: "Years Experience" },
    { icon: Users, value: "5000+", label: "Certified Seafarers" },
    { icon: Globe, value: "50+", label: "Countries Served" },
    { icon: Shield, value: "100%", label: "STCW Compliant" },
  ];

  const trainingLocations = [
    { country: "India", cities: ["Mumbai", "Kochi"], flag: "in" },
    { country: "Sri Lanka", cities: ["Colombo", "Galle"], flag: "lk" },
    { country: "South Africa", cities: ["Cape Town", "Durban"], flag: "za" },
    { country: "Thailand", cities: ["Bangkok", "Phuket"], flag: "th" },
    { country: "Vietnam", cities: ["Ho Chi Minh City", "Hanoi"], flag: "vn" },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section with Fixed Video Background */}
      <section className="relative flex h-screen items-center justify-center pt-20">
        {/* Fixed Video Background */}
        <div className="fixed inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="h-full w-full object-cover"
          >
            <source src="/shipment.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-slate-900/60" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 mx-auto max-w-7xl px-4 text-center text-white sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mx-auto max-w-4xl"
          >
            <h1 className="mb-6 text-4xl font-bold leading-tight sm:text-5xl md:text-7xl">
              Nautilus Times
              <span className="block text-teal-400">
                Maritime People. Practical Solutions.
              </span>
            </h1>
            <p className="mx-auto mb-8 max-w-3xl text-lg text-slate-200 sm:text-xl md:text-2xl">
              The sea is never the same two days in a row – and neither is
              shipping. We help shipowners, managers, seafarers and charterers
              navigate constant change with confidence.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                to="/about"
                className="flex transform items-center justify-center space-x-2 rounded-full bg-teal-600 px-6 py-3 text-base font-semibold text-white transition-all duration-300 hover:scale-105 hover:bg-teal-700 sm:px-8 sm:py-4 sm:text-lg"
              >
                <span>Learn More</span>
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/contact"
                className="rounded-full border-2 border-white px-6 py-3 text-base font-semibold text-white transition-all duration-300 hover:bg-white hover:text-slate-900 sm:px-8 sm:py-4 sm:text-lg"
              >
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section - Overlapping the video */}
      <section className="relative z-20 bg-white pt-16">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-4 sm:gap-8 md:grid-cols-4">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-100 sm:h-16 sm:w-16">
                  <stat.icon className="h-6 w-6 text-teal-600 sm:h-8 sm:w-8" />
                </div>
                <div className="mb-2 text-2xl font-bold text-slate-900 sm:text-3xl">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-600 sm:text-base">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="relative z-10 bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-6 text-3xl font-bold text-slate-900 sm:text-4xl md:text-5xl">
                Who We Are
              </h2>
              <p className="mb-6 text-base text-slate-600 sm:text-lg">
                Nautilus Times is a maritime services and training group focused
                on training & certifications, ship and crew management, and
                technical and commercial support for vessels across different
                segments.
              </p>
              <p className="mb-8 text-base text-slate-600 sm:text-lg">
                We combine operational experience at sea and ashore with a
                strong understanding of regulations, flag requirements and
                commercial pressures. For our clients, this means fewer
                surprises, smoother audits and a more predictable business.
              </p>
              <ul className="mb-8 space-y-3">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Practical, Results-Oriented Approach
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Global Presence with Local Expertise
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Long-term Client Relationships
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-slate-700">
                    Transparent Communication & Reporting
                  </span>
                </li>
              </ul>
              <Link
                to="/about"
                className="inline-flex items-center space-x-2 font-semibold text-teal-600 hover:text-teal-700"
              >
                <span>Learn More About Us</span>
                <ArrowRight size={18} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="/ship.png"
                alt="Maritime Training Facility"
                className="rounded-2xl shadow-2xl"
                height={1000}
                width={1000}
              />
              <div className="absolute -bottom-6 -left-6 rounded-xl bg-teal-600 p-4 text-white sm:p-6">
                <div className="text-xl font-bold sm:text-2xl">5000+</div>
                <div className="text-sm text-teal-100 sm:text-base">
                  Certified Professionals
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Training Categories Section */}
      <section className="relative z-10 bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16 text-center"
          >
            <h2 className="mb-6 text-3xl font-bold text-slate-900 sm:text-4xl md:text-5xl">
              What We Do
            </h2>
            <p className="mx-auto max-w-3xl text-lg text-slate-600 sm:text-xl">
              A connected set of services under one umbrella, designed to
              support your maritime operations
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Link to={service.link}>
                    <div className="group relative h-full overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 shadow-lg transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white transition-transform duration-300 group-hover:scale-110 sm:h-14 sm:w-14">
                        <IconComponent size={28} />
                      </div>
                      <h3 className="mb-3 text-lg font-bold text-slate-900 transition-colors duration-300 group-hover:text-teal-600 sm:text-xl">
                        {service.title}
                      </h3>
                      <p className="mb-6 text-sm leading-relaxed text-slate-600 sm:text-base">
                        {service.description}
                      </p>
                      <ul className="mb-6 space-y-2">
                        {service.highlights.map((highlight, idx) => (
                          <li
                            key={idx}
                            className="flex items-center text-xs text-slate-600 sm:text-sm"
                          >
                            <CheckCircle
                              size={14}
                              className="mr-2 flex-shrink-0 text-teal-500"
                            />
                            {highlight}
                          </li>
                        ))}
                      </ul>
                      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                        <span className="text-sm font-medium text-teal-600">
                          Learn More
                        </span>
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-50 transition-all duration-300 group-hover:scale-110 group-hover:bg-teal-100">
                          <ArrowRight className="h-4 w-4 text-teal-600" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      {/* <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16 text-center z-50"
          >
            <h2 className="mb-6 text-3xl font-bold text-slate-900 sm:text-4xl md:text-5xl">
              How We Work
            </h2>
            <p className="mx-auto max-w-3xl text-lg text-slate-600 sm:text-xl">
              We believe in long-term relationships, not one-time transactions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="rounded-xl bg-white p-8 shadow-lg"
              >
                <h3 className="mb-3 text-lg font-bold text-slate-900 sm:text-xl">
                  {item.title}
                </h3>
                <p className="text-slate-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Training Partner Section */}
      <section className="relative z-10 bg-slate-900 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16 text-center"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Our Trusted Training Partner
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Partnering with accredited institutions to deliver globally
              compliant maritime training.
            </p>
          </motion.div>

          {/* Partner Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-slate-800 rounded-3xl p-10 shadow-xl border border-slate-700"
          >
            {/* Logo + Name */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start sm:space-x-6">
              {/* Logo only */}
              <div className="h-24 w-24 rounded-xl flex items-center justify-center shadow-lg bg-slate-700">
                <img
                  src="/mm.webp"
                  alt="Mekong Marine Logo"
                  className="h-16 w-16 object-contain"
                />
              </div>

              {/* Text Content */}
              <div className="mt-6 sm:mt-0 space-y-4">
                <h3 className="text-3xl font-bold text-white">Mekong Marine</h3>

                <p className="text-slate-300 text-lg leading-relaxed max-w-2xl">
                  Mekong Marine delivers internationally compliant training
                  programs backed by experienced instructors, modern simulation
                  systems, and high-standard maritime facilities.
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-3 pt-2">
                  <span className="bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm">
                    STCW Certified
                  </span>
                  <span className="bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm">
                    Offshore Competency
                  </span>
                  <span className="bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm">
                    Global Accreditation
                  </span>
                </div>

                {/* Button */}
                <a
                  href="https://mekongmarine.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-6 text-teal-400 font-semibold hover:text-teal-300"
                >
                  Visit Mekong Marine
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Training Locations */}
      <section className="relative z-10 bg-teal-600 py-20 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16 text-center"
          >
            <h2 className="mb-6 text-3xl font-bold sm:text-4xl md:text-5xl">
              Global Training Locations
            </h2>
            <p className="mx-auto max-w-3xl text-lg text-teal-100 sm:text-xl">
              Train at our world-class facilities located in major maritime hubs
              across the globe.
            </p>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-6">
            {trainingLocations.map((location, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="w-full rounded-2xl bg-white/10 p-4 backdrop-blur-sm transition-all duration-300 hover:bg-white/20 sm:w-[48%] sm:p-6 lg:w-[30%]"
              >
                <div className="mb-4 flex items-center justify-center space-x-3">
                  <span className="text-2xl sm:text-3xl">
                    <img
                      src={`https://flagcdn.com/48x36/${location.flag}.png`}
                      srcSet={`https://flagcdn.com/96x72/${location.flag}.png 2x, https://flagcdn.com/144x108/${location.flag}.png 3x`}
                      width="48"
                      height="36"
                      alt={`${location.country} flag`}
                      className="mb-2 inline-block"
                    />
                  </span>
                  <h3 className="text-lg font-bold sm:text-xl">
                    {location.country}
                  </h3>
                </div>
                <div className="space-y-2">
                  {location.cities.map((city, cityIndex) => (
                    <div
                      key={cityIndex}
                      className="flex items-center justify-center space-x-2"
                    >
                      <MapPin className="h-4 w-4 text-teal-200" />
                      <span className="text-sm text-teal-100 sm:text-base">
                        {city}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 bg-slate-900 py-20 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-6 text-3xl font-bold sm:text-4xl md:text-5xl">
                Ready to Start Your Maritime Journey?
              </h2>
              <p className="mb-8 text-lg text-slate-300 sm:text-xl">
                Join thousands of maritime professionals who have advanced their
                careers with Nautilus Times. Contact us today to learn more
                about our programs and services.
              </p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/contact"
                  className="rounded-full bg-teal-600 px-6 py-3 text-center font-semibold text-white transition-colors duration-300 hover:bg-teal-700 sm:px-8 sm:py-4"
                >
                  Contact Us
                </Link>
                <Link
                  to="/payment"
                  className="rounded-full border-2 border-white px-6 py-3 text-center font-semibold text-white transition-all duration-300 hover:bg-white hover:text-slate-900 sm:px-8 sm:py-4"
                >
                  Enroll Now
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
